import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import NewsAudienceLabel from '../components/shared/NewsAudienceLabel.jsx';
import DepartmentLabels from '../components/shared/DepartmentLabel.jsx';

// A sportcomplex's item: which of its clubs' members see it (in the complex), or where it
// comes from (in a club, where it is read only)
const COMPLEX_AUDIENCE = { besturen: 'Voor de besturen van de clubs', leden: 'Voor alle leden van de clubs' };
function ComplexLabel({ item }) {
  const text = item.from_complex ? `🏟 ${item.from_complex}` : COMPLEX_AUDIENCE[item.complex_audience];
  if (!text) return null;
  return <span style="font-size:11px;background:#ecfdf5;color:#047857;padding:2px 8px;border-radius:10px;font-weight:500">{text}</span>;
}

// In a sportcomplex: for the besturen of the linked clubs or for all their members
function ComplexAudiencePicker({ value, onChange }) {
  return (
    <div class="form-group">
      <label class="form-label">Voor wie</label>
      {Object.entries(COMPLEX_AUDIENCE).map(([key, label]) => (
        <label key={key} style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;margin-bottom:4px">
          <input type="radio" name="complex-audience" checked={value === key} onChange={() => onChange(key)} />
          {label}
        </label>
      ))}
      <div class="text-sm text-muted">Besturen: de clubbeheerders en de contactpersonen die elke club heeft aangewezen. Het bericht staat in het nieuws van de clubs, met een melding.</div>
    </div>
  );
}

function formatDate(str) {
  if (!str) return '';
  return parseServerDate(str).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

const labelStyle = 'display:block;font-size:13px;font-weight:500;margin-bottom:6px;color:var(--color-text-muted)';
const checkStyle = 'width:16px;height:16px;accent-color:var(--color-primary);flex-shrink:0';

// "Voor wie": everyone, or picked sections, commissions and categories (functions/teams).
// value = [{ type, id }]; a section row opens to show its commissions and categories.
// options comes from /api/news/audience-options: the whole club for the editors, only their
// own groups for members (who can't choose "Iedereen": canEveryone false).
// options undefined = still loading, null = could not load.
function AudiencePicker({ options, canEveryone, mode, onMode, value, onChange }) {
  const [open, setOpen] = useState(() => new Set());
  // Editing: open the sections that hold a picked commission or category, once the groups are in
  useEffect(() => {
    if (!options) return;
    setOpen(new Set([
      ...options.commissions.filter(c => value.some(v => v.type === 'commission' && v.id === c.id)).map(c => c.section_id),
      ...options.categories.filter(c => value.some(v => v.type === 'category' && v.id === c.id)).map(c => c.section_id),
    ]));
  }, [options]);

  const has = (type, id) => value.some(v => v.type === type && v.id === id);
  const toggle = (type, id) => onChange(has(type, id) ? value.filter(v => !(v.type === type && v.id === id)) : [...value, { type, id }]);
  const toggleOpen = id => setOpen(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  // Picked groups below a section count, so a closed section still shows that something inside is chosen
  const pickedInside = s => value.filter(v =>
    (v.type === 'commission' && options.commissions.some(c => c.id === v.id && c.section_id === s.id)) ||
    (v.type === 'category' && options.categories.some(c => c.id === v.id && c.section_id === s.id))).length;

  const check = (type, id, name, bold = false) => (
    <label key={`${type}${id}`} style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;padding:3px 0">
      <input type="checkbox" checked={has(type, id)} onChange={() => toggle(type, id)} style={checkStyle} />
      <span style={bold ? 'font-weight:600' : ''}>{name}</span>
    </label>
  );

  return (
    <div>
      <label style={labelStyle}>{canEveryone ? 'Voor wie' : 'Voor welke groep(en)'}</label>
      {canEveryone && <div style="display:flex;gap:20px;flex-wrap:wrap;margin-bottom:8px">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px">
          <input type="radio" name="news-audience" checked={mode === 'everyone'} onChange={() => onMode('everyone')} style={checkStyle} />
          <span>Iedereen</span>
        </label>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px">
          <input type="radio" name="news-audience" checked={mode === 'groups'} onChange={() => onMode('groups')} style={checkStyle} />
          <span>Bepaalde groepen</span>
        </label>
      </div>}

      {mode === 'groups' && (
        <div style="border:1px solid var(--color-border);border-radius:8px;padding:10px 12px;max-height:320px;overflow-y:auto">
          <p style="font-size:12px;color:var(--color-text-muted);margin:0 0 8px">
            {canEveryone ? '' : 'Je kunt posten voor de groepen waar je zelf bij hoort. '}
            Alleen leden met een functie in een gekozen sectie, commissie of functie/team zien dit bericht. Nieuwsbeheerders zien alles.
          </p>
          {options === undefined ? (
            <p style="font-size:13px;color:var(--color-text-muted);margin:0">Laden…</p>
          ) : !options ? (
            <p style="font-size:13px;color:#991b1b;margin:0">Kon de groepen niet laden.</p>
          ) : options.sections.length === 0 ? (
            <p style="font-size:13px;color:var(--color-text-muted);margin:0">{canEveryone ? 'Er zijn nog geen secties ingericht.' : 'Je hoort nog bij geen sectie of commissie.'}</p>
          ) : options.sections.map(s => {
            const commissions = options.commissions.filter(c => c.section_id === s.id);
            const looseCategories = options.categories.filter(c => c.section_id === s.id && !c.commission_id);
            const hasChildren = commissions.length > 0 || looseCategories.length > 0;
            const inside = pickedInside(s);
            return (
              <div key={s.id} style="border-bottom:1px solid var(--color-border);padding:4px 0">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
                  {check('section', s.id, s.name, true)}
                  {hasChildren && (
                    <button type="button" onClick={() => toggleOpen(s.id)} aria-expanded={open.has(s.id)}
                      style="background:none;border:none;color:var(--color-primary);font-size:12px;cursor:pointer;padding:4px;white-space:nowrap">
                      {inside > 0 && !open.has(s.id) ? `${inside} gekozen · ` : ''}{open.has(s.id) ? 'Minder ▴' : 'Commissies & functies ▾'}
                    </button>
                  )}
                </div>
                {open.has(s.id) && (
                  <div style="padding-left:24px">
                    {commissions.map(c => (
                      <div key={`c${c.id}`}>
                        {check('commission', c.id, c.name)}
                        <div style="padding-left:24px">
                          {options.categories.filter(cat => cat.commission_id === c.id).map(cat => (
                            check('category', cat.id, cat.name)
                          ))}
                        </div>
                      </div>
                    ))}
                    {looseCategories.map(cat => check('category', cat.id, cat.name))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {mode === 'groups' && value.length === 0 && (
        <p style="font-size:12px;color:#92400e;margin:6px 0 0">{canEveryone ? 'Kies minstens één groep, of zet het bericht op Iedereen.' : 'Kies minstens één groep.'}</p>
      )}
    </div>
  );
}

function NewsForm({ initial, onSave, onCancel, saving, options, canEveryone, complex }) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    content: initial?.content || '',
    image_url: initial?.image_url || '',
    is_pinned: initial?.is_pinned ? true : false,
    is_published: initial?.is_published !== 0
  });
  // A deleted group can't be picked again; the item stays "for groups" so saving needs a real choice
  const [audience, setAudience] = useState(() => (initial?.audience || []).filter(a => !a.deleted).map(({ type, id }) => ({ type, id })));
  const [audienceMode, setAudienceMode] = useState(initial?.audience?.length || !canEveryone ? 'groups' : 'everyone');
  const audienceMissing = audienceMode === 'groups' && audience.length === 0;
  // Members get a notification once, when the item first goes online; after that there is nothing to choose
  const [notify, setNotify] = useState(true);
  const canNotify = !initial?.notified_at;
  const [complexAudience, setComplexAudience] = useState(initial?.complex_audience || 'besturen');
  const [mailGuests, setMailGuests] = useState(initial ? !!initial.mail_guests : true);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  return (
    <div style="background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px;padding:24px;margin-bottom:24px">
      <h3 style="font-size:16px;font-weight:600;margin:0 0 16px">
        {initial ? 'Bericht bewerken' : 'Nieuw bericht'}
      </h3>

      <div style="display:flex;flex-direction:column;gap:14px">
        <div>
          <label style="display:block;font-size:13px;font-weight:500;margin-bottom:6px;color:var(--color-text-muted)">Titel *</label>
          <input
            class="form-input"
            value={form.title}
            onInput={e => set('title', e.target.value)}
            placeholder="Bijv: Uitnodiging Jaarvergadering"
            maxLength={200}
          />
        </div>

        <div>
          <label style="display:block;font-size:13px;font-weight:500;margin-bottom:6px;color:var(--color-text-muted)">Bericht *</label>
          <textarea
            class="form-input"
            style="min-height:120px;resize:vertical"
            value={form.content}
            onInput={e => set('content', e.target.value)}
            placeholder="Schrijf hier je bericht..."
          />
          <AiAssist kind="news_content" value={form.content} context={{ title: form.title }} onUse={text => set('content', text)} />
        </div>

        <div>
          <label style="display:block;font-size:13px;font-weight:500;margin-bottom:6px;color:var(--color-text-muted)">Afbeelding URL (optioneel)</label>
          <input
            class="form-input"
            value={form.image_url}
            onInput={e => set('image_url', e.target.value)}
            placeholder="https://..."
          />
        </div>

        {complex
          ? <>
              <ComplexAudiencePicker value={complexAudience} onChange={setComplexAudience} />
              {canNotify && canEveryone && (
                <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px;margin:-6px 0 14px">
                  <input type="checkbox" checked={mailGuests} onChange={e => setMailGuests(e.target.checked)} />
                  Ook per mail naar de gastclubs (contactpersonen van clubs zonder Vereniging4U)
                </label>
              )}
            </>
          : <AudiencePicker options={options} canEveryone={canEveryone} mode={audienceMode} onMode={setAudienceMode} value={audience} onChange={setAudience} />}

        {/* Pinning and drafts are for the editors; a member's item goes online at once */}
        {canEveryone && <div style="display:flex;gap:24px">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px">
            <input
              type="checkbox"
              checked={form.is_pinned}
              onChange={e => set('is_pinned', e.target.checked)}
              style="width:16px;height:16px;accent-color:var(--color-primary)"
            />
            <span>📌 Vastpinnen bovenaan</span>
          </label>

          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:14px">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={e => set('is_published', e.target.checked)}
              style="width:16px;height:16px;accent-color:var(--color-primary)"
            />
            <span>Gepubliceerd</span>
          </label>
        </div>}

        {canNotify && (
          <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:14px">
            <input
              type="checkbox"
              checked={notify}
              onChange={e => setNotify(e.target.checked)}
              style="width:16px;height:16px;margin-top:2px;accent-color:var(--color-primary)"
            />
            <span>
              🔔 Leden een melding sturen
              <span style="display:block;font-size:12px;color:var(--color-text-muted)">
                {canEveryone && !form.is_published
                  ? 'De melding gaat zodra je het bericht publiceert.'
                  : 'In de app en als pushbericht, één keer, voor iedereen voor wie het bericht is.'}
              </span>
            </span>
          </label>
        )}
      </div>

      <div style="display:flex;gap:8px;margin-top:20px">
        <Button
          variant="primary"
          size="sm"
          onClick={() => onSave(complex
            ? { ...form, audience: [], complex_audience: complexAudience, notify, mail_guests: mailGuests }
            : canEveryone
              ? { ...form, audience: audienceMode === 'groups' ? audience : [], notify }
              : { title: form.title, content: form.content, image_url: form.image_url, audience, notify })}
          disabled={saving || !form.title.trim() || !form.content.trim() || (!complex && audienceMissing)}
        >
          {saving ? 'Opslaan...' : 'Opslaan'}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleren</Button>
      </div>
    </div>
  );
}

function NewsItemRow({ item, onEdit, onDelete, deleting, canEdit, canDelete }) {
  return (
    <div style="display:flex;align-items:flex-start;gap:16px;padding:16px;border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface)">
      {item.image_url && (
        <img
          src={item.image_url}
          alt=""
          style="width:64px;height:64px;object-fit:cover;border-radius:8px;flex-shrink:0"
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}
      <div style="flex:1;min-width:0">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap">
          {item.is_pinned ? (
            <span style="font-size:11px;background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:10px;font-weight:500">📌 Vastgepind</span>
          ) : null}
          {!item.is_published ? (
            <span style="font-size:11px;background:#f3f4f6;color:#6b7280;padding:2px 8px;border-radius:10px;font-weight:500">Concept</span>
          ) : (
            <span style="font-size:11px;background:#dcfce7;color:#166534;padding:2px 8px;border-radius:10px;font-weight:500">Gepubliceerd</span>
          )}
          <DepartmentLabels targets={item.audience} />
          <NewsAudienceLabel audience={item.audience} />
          <ComplexLabel item={item} />
        </div>
        <div style="font-weight:600;font-size:15px;margin-bottom:4px">{item.title}</div>
        <div style="font-size:13px;color:var(--color-text-muted);overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical">
          {item.content}
        </div>
        <div style="font-size:12px;color:var(--color-text-muted);margin-top:6px">
          {formatDate(item.published_at)} · {item.author_name || 'Onbekend'}
        </div>
      </div>
      <div style="display:flex;gap:6px;flex-shrink:0">
        {canEdit && <Button variant="secondary" size="sm" onClick={() => onEdit(item)}>Bewerken</Button>}
        {canDelete && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => onDelete(item.id)}
            disabled={deleting === item.id}
          >
            {deleting === item.id ? '...' : 'Verwijderen'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function NewsView() {
  const auth = useAuth();
  // Each button follows its own news right; club settings managers may do everything
  const isAdmin = auth.hasPermission('settings', 'update');
  const canCreate = isAdmin || auth.hasPermission('news', 'create');
  const canEdit = isAdmin || auth.hasPermission('news', 'update');
  const canDelete = isAdmin || auth.hasPermission('news', 'delete');
  const canManage = canCreate || canEdit || canDelete;
  const canRead = canManage || auth.hasPermission('news', 'read');
  // An item of the sportcomplex the club is housed at: read only here
  const mine = item => !item.from_complex && item.created_by === auth.userId;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  // What this user may post and for which groups (members: only their own groups)
  const [writeOpts, setWriteOpts] = useState(undefined);
  // Editors (create or update) choose freely; a member stays within their own groups
  const canEveryone = canCreate || canEdit;
  const canPost = canEveryone || !!writeOpts?.can_post;
  const ownGroup = t => !!writeOpts && ({ section: writeOpts.sections, commission: writeOpts.commissions, category: writeOpts.categories })[t.type]?.some(g => g.id === t.id);
  // A member edits their own item while it is still for their own groups (same rule as the server)
  const mayEdit = item => !item.from_complex && (canEdit || (mine(item) && (canCreate || (canPost && item.audience?.length > 0 && item.audience.every(ownGroup)))));
  const mayDelete = item => !item.from_complex && (canDelete || (mine(item) && canRead));

  async function load() {
    setLoading(true);
    try {
      const endpoint = canManage ? '/api/news/all' : '/api/news';
      const data = await api(endpoint);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError('Kon nieuws niet laden.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    if (canRead) api('/api/news/audience-options').then(setWriteOpts).catch(() => setWriteOpts(null));
    else setWriteOpts(null);
  }, []);

  async function handleSave(form) {
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        const updated = await api(`/api/news/${editItem.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      } else {
        const created = await api('/api/news', { method: 'POST', body: JSON.stringify(form) });
        setItems(prev => [created, ...prev]);
      }
      setShowForm(false);
      setEditItem(null);
    } catch {
      setError('Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!confirm('Weet je zeker dat je dit bericht wilt verwijderen?')) return;
    setDeleting(id);
    try {
      await api(`/api/news/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      setError('Verwijderen mislukt.');
    } finally {
      setDeleting(null);
    }
  }

  function startEdit(item) {
    setEditItem(item);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelForm() {
    setShowForm(false);
    setEditItem(null);
  }

  return (
    <div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;flex-wrap:wrap;gap:12px">
        <div>
          <h1 class="page-title" style="margin:0 0 4px">Nieuws</h1>
          <p style="font-size:14px;color:var(--color-text-muted);margin:0">
            {auth.isComplex ? 'Mededelingen aan de clubs op het complex.' : canManage ? 'Beheer nieuwsberichten voor jouw club.' : 'Nieuws van jouw club.'}
          </p>
        </div>
        {canPost && !showForm && (
          <Button variant="primary" onClick={() => { setEditItem(null); setShowForm(true); }}>
            + Nieuw bericht
          </Button>
        )}
      </div>

      {error && (
        <div style="background:#fee2e2;color:#991b1b;padding:12px 16px;border-radius:8px;margin-bottom:16px;font-size:14px">
          {error}
        </div>
      )}

      {showForm && (editItem ? mayEdit(editItem) : canPost) && (
        <NewsForm
          key={editItem?.id ?? 'new'}
          initial={editItem}
          onSave={handleSave}
          onCancel={cancelForm}
          saving={saving}
          options={writeOpts}
          canEveryone={canEveryone}
          complex={auth.isComplex}
        />
      )}

      {loading ? (
        <div style="display:flex;flex-direction:column;gap:12px">
          {[0,1,2].map(i => (
            <div key={i} class="skeleton" style="height:100px;border-radius:10px" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <div style="text-align:center;padding:48px 24px;color:var(--color-text-muted)">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 12px;display:block;opacity:0.4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
            </svg>
            <p style="font-size:15px;font-weight:500;margin:0 0 4px">Nog geen berichten</p>
            <p style="font-size:13px;margin:0">
              {canPost ? 'Maak je eerste nieuwsbericht aan via de knop hierboven.' : 'Er zijn nog geen berichten geplaatst.'}
            </p>
          </div>
        </Card>
      ) : (
        <div style="display:flex;flex-direction:column;gap:12px">
          {items.map(item => (
            canManage ? (
              <NewsItemRow
                key={`${item.from_complex ? 'c' : ''}${item.id}`}
                item={item}
                onEdit={startEdit}
                onDelete={handleDelete}
                deleting={deleting}
                canEdit={mayEdit(item)}
                canDelete={mayDelete(item)}
              />
            ) : (
              <NewsCard
                key={`${item.from_complex ? 'c' : ''}${item.id}`}
                item={item}
                onEdit={mayEdit(item) ? startEdit : null}
                onDelete={mayDelete(item) ? handleDelete : null}
                deleting={deleting}
              />
            )
          ))}
        </div>
      )}
    </div>
  );
}

// News card for regular members; their own items get edit and delete
function NewsCard({ item, onEdit, onDelete, deleting }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = item.content.length > 200;

  return (
    <div style={`border:1px solid var(--color-border);border-radius:12px;overflow:hidden;background:var(--color-surface);${item.is_pinned ? 'border-left:4px solid var(--color-primary)' : ''}`}>
      {item.image_url && (
        <img
          src={item.image_url}
          alt=""
          style="width:100%;height:180px;object-fit:cover;display:block"
          onError={e => { e.target.style.display = 'none'; }}
        />
      )}
      <div style="padding:20px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap">
          {!!item.is_pinned && (
            <span style="font-size:11px;background:var(--color-primary);color:#fff;padding:2px 8px;border-radius:10px;font-weight:500">📌 Vastgepind</span>
          )}
          <DepartmentLabels targets={item.audience} />
          <NewsAudienceLabel audience={item.audience} />
          <ComplexLabel item={item} />
          <span style="font-size:12px;color:var(--color-text-muted)">{formatDate(item.published_at)}</span>
        </div>
        <h3 style="font-size:17px;font-weight:700;margin:0 0 8px;color:var(--color-text)">{item.title}</h3>
        <p style={`font-size:14px;color:var(--color-text-muted);margin:0;line-height:1.6;${!expanded && isLong ? 'overflow:hidden;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical' : ''}`}>
          {item.content}
        </p>
        {isLong && (
          <button
            onClick={() => setExpanded(e => !e)}
            style="background:none;border:none;color:var(--color-primary);font-size:13px;font-weight:500;cursor:pointer;padding:8px 0 0;display:block"
          >
            {expanded ? 'Minder tonen ↑' : 'Meer lezen →'}
          </button>
        )}
        {(item.author_name || onEdit || onDelete) && (
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;font-size:12px;color:var(--color-text-muted);margin-top:12px;padding-top:12px;border-top:1px solid var(--color-border)">
            <span>{item.author_name ? `Door ${item.author_name}` : ''}</span>
            {(onEdit || onDelete) && (
              <div style="display:flex;gap:6px">
                {onEdit && <Button variant="secondary" size="sm" onClick={() => onEdit(item)}>Bewerken</Button>}
                {onDelete && (
                  <Button variant="danger" size="sm" onClick={() => onDelete(item.id)} disabled={deleting === item.id}>
                    {deleting === item.id ? '...' : 'Verwijderen'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
